"use client";

import { type Gift } from "@/types";
import { SpotifyExperienceSections } from "@/components/published-experiences/SpotifyExperienceSections";
import {
  SpotifyLovePage,
  getSpotifyPalette,
} from "@/components/wrapped/SpotifyLovePage";

// IDs de tema oferecidos pelo wizard que mapeiam para o layout principal
// (estilo Spotify). Qualquer outro slug é renderizado também como Spotify
// usando "purple-romantic" como fallback.
const SPOTIFY_THEME_SLUGS = new Set([
  "purple-romantic",
  "midnight-rose",
  "deep-wine",
  "ocean-love",
  "sweet-pink",
  // Tema custom — usuário subiu uma imagem própria no StepTheme. Dois
  // sabores: opaco (blur ambiente) e nítido (pôster cheio no topo).
  "custom-photo",
  "custom-photo-sharp",
]);

interface PublicGiftRendererProps {
  gift: Partial<Gift>;
  // shareUrl / hideSharing são mantidos na assinatura por compatibilidade com
  // chamadores antigos. O layout Spotify cuida do compartilhamento internamente,
  // então estes campos não têm efeito direto aqui.
  shareUrl?: string;
  hideSharing?: boolean;
  // Quando true, suprime o autoplay da música. Usado nos previews internos
  // do wizard (mockup lateral, modal "Ver como vai ficar") pra não ter
  // várias faixas tocando em paralelo. A página publicada real NÃO passa
  // esse flag — autoplay segue valendo lá.
  disableMusicAutoplay?: boolean;
}

// Componente único de renderização da ILovePage. Hoje o único layout ativo é o
// estilo Spotify (SpotifyLovePage). Os templates legados foram desativados;
// gifts antigos com slug fora do conjunto Spotify também caem aqui, com slug
// efetivo "purple-romantic".
export function PublicGiftRenderer({
  gift,
  disableMusicAutoplay,
}: PublicGiftRendererProps) {
  const isSpotifyTheme = SPOTIFY_THEME_SLUGS.has(gift.templateSlug ?? "");
  const effectiveSpotifySlug = isSpotifyTheme
    ? (gift.templateSlug as string)
    : "purple-romantic";
  const palette = getSpotifyPalette(effectiveSpotifySlug);
  const photoUrls = (gift.photos ?? []).map((p) => p.url);
  const hasExtras = (gift.selectedExperiences?.length ?? 0) > 0;

  return (
    <div className="min-h-screen" style={{ background: "#121212" }}>
      <SpotifyLovePage
        disableMusicAutoplay={disableMusicAutoplay}
        data={{
          partnerName: gift.partnerName ?? undefined,
          recipientName: gift.recipientName ?? undefined,
          title: gift.title,
          coupleStartDate: gift.coupleStartDate,
          loveLetter: gift.loveLetter ?? undefined,
          musicName: gift.musicName ?? undefined,
          artistName: gift.artistName ?? undefined,
          musicUrl: gift.musicUrl ?? undefined,
          musicPreviewUrl: gift.musicPreviewUrl ?? undefined,
          photoUrls,
          templateSlug: effectiveSpotifySlug,
          coverPhotoUrl: gift.coverPhotoUrl ?? undefined,
          // Artwork da música escolhida pela busca de Spotify/iTunes —
          // vira a capa central do "álbum". Separada do coverPhotoUrl
          // (que é a imagem custom do tema, vai pro background).
          musicArtworkUrl: gift.artworkUrl ?? undefined,
          // Só passa extraSections quando há experiências selecionadas para
          // evitar o cabeçalho "Mais para vocês" vazio.
          extraSections: hasExtras ? (
            <SpotifyExperienceSections
              selectedExperiences={gift.selectedExperiences}
              palette={palette}
              gift={gift}
            />
          ) : undefined,
        }}
      />
    </div>
  );
}
